import { Location } from '@angular/common';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LsqListSource } from '@lsq-frontend/angular-shared/api-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import { SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { InvoiceFundingStatus } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    RevokeChildInvoiceRequest,
    RevokeChildInvoiceService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/revoke-child-invoice/revoke-child-invoice';
import { BuyerAccountInvoiceGroupDetail } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-account-invoice-group-detail';
import { BuyerAccountInvoiceGroupItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-account-invoice-group-item';
import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    GetBuyerAccountInvoiceGroupRequest,
    GetBuyerAccountInvoiceGroupService
} from '@lsq-frontend/remote-services/financing-graphql';
import { Color, Modal, StatusColor } from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    Icons,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, pluck, shareReplay, take } from 'rxjs/operators';
import { InvoiceFundingStatusModifications } from '../../../seller/invoices/invoices-list/invoices-list.component';
import { InvoiceFundingStatusDisplay } from '../../../supply-chain-direct.models';
import { BUYER_PAYMENT_DETAILS } from '../../payment-details/payment-detail.component';
import { SupplyChainDirectAccountService } from '../../supply-chain-direct-account.service';
import { SupplyChainDirectBuyerAccountSharedService } from '../../supply-chain-direct-buyer-account-shared.service';
import { PENDING_BUYER_CREDIT_MEMOS_TAB } from '../pending-credit-memos/pending-credit-memos.component';
import { RevokeInvoiceModalComponent } from '../revoke-invoice-modal/revoke-invoice-modal.component';

@Component({
    selector: 'account-parent-grouped-invoice-detail',
    templateUrl: './parent-grouped-invoice-detail.component.html',
    styleUrls: ['./parent-grouped-invoice-detail.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AccountParentGroupedInvoiceDetailPageComponent implements OnInit {
    public readonly revokeButtonIsDisabledBound = this._revokeButtonIsDisabled.bind(this);
    public readonly revokeButtonIsHiddenBound = this._revokeButtonIsHidden.bind(this);
    public readonly transformStatusBound = this._transformStatus.bind(this);
    public isLoading = true;

    public readonly ineligibleStatus = InvoiceFundingStatus.INELIGIBLE;

    public readonly invoiceFundingStatus = InvoiceFundingStatus;

    public readonly color = Color;
    public readonly statusColor = StatusColor;
    public readonly icons = Icons;

    public dataSource: LsqListSource<BuyerAccountInvoiceGroupItem>;
    public summaryDataSource: LsqListSource<BuyerAccountInvoiceGroupDetail>;
    public invoiceGroup: BuyerAccountInvoiceGroupDetail;
    public backButtonText$: Observable<string>;
    public readonly processingColor = Color.Yellow;

    public readonly creditMemoStatus = InvoiceFundingStatusModifications.CREDIT_MEMO;

    public readonly paymentTotalI18n = this._i18n('Payment Total');
    public readonly statusI18n = this._i18n('Status');
    public readonly positiveInvoicesI18n = this._i18n('Positive Invoices');
    public readonly creditAmountI18n = this._i18n('Credit Amount');
    public readonly revokeHeaderI18n = this._i18n({ value: 'Revoke' });
    public readonly revokeModalTitleI18n = this._i18n({ value: 'Revoke Invoice' });
    public readonly revokeGroupModalTitleI18n = this._i18n({ value: 'Revoke Invoices' });

    public readonly supplierI18n = this._i18n({
        value: 'Supplier',
        meaning: `The supplier for the invoice`,
        description: 'Table column header'
    });
    public readonly invoiceNumberI18n = this._i18n({
        value: 'Invoice Number',
        meaning: `Invoice number`,
        description: 'Table column header'
    });
    public readonly invoiceDateI18n = this._i18n({
        value: 'Invoice Date',
        meaning: `Invoice date`,
        description: 'Table column header'
    });

    public readonly invoiceDueDateI18n = this._i18n({
        value: 'Due Date',
        meaning: `Due date`,
        description: 'Table column header'
    });

    public readonly dueInI18n = this._i18n({
        value: 'Due In',
        meaning: `Days until the invoice is due to be paid by the buyer`,
        description: 'Table column header'
    });
    public readonly invoiceAmountI18n = this._i18n({
        value: 'Amount',
        meaning: `The money value of the invoice`,
        description: 'Table column header'
    });
    public readonly invoiceStatusI18n = this._i18n({
        value: 'Status',
        meaning: `The eligibility status of the invoice`,
        description: 'Table column header'
    });

    public readonly invoicesBackText = this._i18n('Invoices');
    public readonly pendingCreditMemosBackText = this._i18n('Pending Credit Memos');
    public readonly paymentBackText = this._i18n('Payment');
    public readonly paymentDateI18n = this._i18n('Payment Date');
    public readonly backText = this._i18n('Back');
    public showPaymentdata$: Observable<boolean>;

    public readonly invoiceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 29;
            }

            if (dimensions.width > 1375) {
                return 18;
            }

            if (dimensions.width > 1199) {
                return 14;
            }
            return 15;
        })
    );

    constructor(
        private _i18n: I18n,
        public location: Location,
        public invoiceFundingStatusDisplay: InvoiceFundingStatusDisplay,
        public responsiveService: ResponsiveService,
        public windowReferenceService: WindowReferenceService,
        public getBuyerAccountInvoiceGroupService: GetBuyerAccountInvoiceGroupService,
        public buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        public buyerAccountService: SupplyChainDirectAccountService,
        private _activatedRoute: ActivatedRoute,
        private _modalService: ModalService,
        private _claimService: ClaimService,
        private _revokeChildInvoiceService: RevokeChildInvoiceService,
        private _featureFlagService: FeatureFlagService
    ) {}

    public async ngOnInit(): Promise<void> {
        await this.loadData();

        this.showPaymentdata$ = this.summaryDataSource.rows$.pipe(
            map((summaryRows) => !!summaryRows[0]?.paymentDate)
        );

        this.backButtonText$ = this._activatedRoute.queryParams.pipe(
            pluck(FastTrackRouteQueryParams.PreviousRouteId),
            map((previousRouteId) => {
                if (previousRouteId === SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES) {
                    return this.invoicesBackText;
                } else if (previousRouteId === PENDING_BUYER_CREDIT_MEMOS_TAB) {
                    return this.pendingCreditMemosBackText;
                } else if (previousRouteId === BUYER_PAYMENT_DETAILS) {
                    return this.paymentBackText;
                } else {
                    return this.backText;
                }
            }),
            shareReplay(1)
        );
    }

    private async loadData(): Promise<void> {
        this.isLoading = true;
        const params = await this._activatedRoute.params.pipe(take(1)).toPromise();
        const queryParams = await this._activatedRoute.queryParams.pipe(take(1)).toPromise();
        const invoiceKey = params.invoiceKey;
        const isPendingCreditMemo =
            queryParams[FastTrackRouteQueryParams.PreviousRouteId] ===
            PENDING_BUYER_CREDIT_MEMOS_TAB;

        const response = await this.getBuyerAccountInvoiceGroupService.executeOnce(
            new GetBuyerAccountInvoiceGroupRequest({
                buyerKeys:
                    this.buyerAccountService.buyerKeysFormGroup.get('buyerKeys')?.value ?? [],
                groupKey: invoiceKey,
                pendingCreditMemosOnly: isPendingCreditMemo
            })
        );

        this.invoiceGroup = response.invoiceGroup as BuyerAccountInvoiceGroupDetail;

        let rows: BuyerAccountInvoiceGroupItem[] = [];
        rows = this.invoiceGroup.items || [];

        this.summaryDataSource = new LsqListSource({
            rows$: of([
                {
                    ...this.invoiceGroup,
                    credits: {
                        ...this.invoiceGroup.credits,
                        cents: this.invoiceGroup.credits?.cents
                            ? this.invoiceGroup.credits.cents * -1
                            : 0
                    }
                } as BuyerAccountInvoiceGroupDetail
            ])
        });

        this.dataSource = new LsqListSource({
            rows$: of(rows)
        });

        this.isLoading = false;
    }

    public async handleRevokeClick(row: BuyerAccountInvoiceGroupItem): Promise<void> {
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);
        this._modalService.showModal(
            new Modal({
                id: 'revoke-invoice',
                title: this.revokeModalTitleI18n,
                component: RevokeInvoiceModalComponent,
                width: '60rem',
                showCloseButton: true,
                data: {
                    buyerInvoice: {
                        refGroupedInvoiceKey: this.invoiceGroup.groupKey,
                        sellerName: this.invoiceGroup.sellerName,
                        invoiceNumber: row.invoiceNumber,
                        groupId: this.invoiceGroup.invoiceNumber,
                        invoiceDate: row.invoiceDate,
                        invoiceDueDate: row.dueDate,
                        finalApprovedAmount: row.amount
                    },
                    childInvoices: []
                },
                isSubmitting$: isSubmittingSubject.asObservable(),
                onOkClick: async (): Promise<void> => {
                    isSubmittingSubject.next(true);
                    const isDissolved = await this._revokeChildInvoiceService.executeOnce(
                        new RevokeChildInvoiceRequest({
                            childInvoiceKey: row.itemKey
                        })
                    );
                    isSubmittingSubject.next(false);
                    this._modalService.closeModal();

                    if (isDissolved.dissolved) this.location.back();
                    else await this.loadData();
                }
            })
        );
    }

    private _revokeButtonIsDisabled(): boolean {
        return (
            !this._claimService.isOnSync(DashboardClaim.FinancingAccountPortalEditInvoices) &&
            !this._claimService.isOnSync(DashboardClaim.FinancingBuyerPortalEditInvoices)
        );
    }

    public canRevokeFeatureFlag$ = this._featureFlagService.isOn(FeatureFlags.RevokeCreditMemo);

    private _revokeButtonIsHidden(row: BuyerAccountInvoiceGroupItem): boolean {
        return !(row.canRevoke as boolean);
    }
    private _transformStatus(row: BuyerAccountInvoiceGroupItem): string {
        return (row.amount?.cents || 0) < 0 && row.status !== 'REVOKED'
            ? this.creditMemoStatus
            : row.status || 'UNKNOWN_STATUS';
    }

    public navigateBack(): void {
        this.location.back();
    }
}
